import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { TripCard } from '../components/TripCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Filter, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TripsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('destination') || '');
  const [tripType, setTripType] = useState(searchParams.get('type') || 'all');

  useEffect(() => {
    // Update tripType from URL params when they change
    const typeFromUrl = searchParams.get('type');
    if (typeFromUrl && typeFromUrl !== tripType) {
      setTripType(typeFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchTrips();
  }, [tripType]);

  const fetchTrips = async () => {
    setLoading(true);
    try {
      const params = {};
      if (tripType && tripType !== 'all') params.trip_type = tripType;
      if (search) params.destination = search;
      
      const res = await axios.get(`${API}/trips`, { params });
      setTrips(res.data);
    } catch (err) {
      console.error('Failed to fetch trips:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTrips();
  };

  const getPageTitle = () => {
    switch(tripType) {
      case 'group': return 'Group Tours';
      case 'solo': return 'Solo Adventures';
      default: return 'Explore Our Adventures';
    }
  };

  const getPageDescription = () => {
    switch(tripType) {
      case 'group': return 'Join our scheduled group tours and travel with like-minded adventurers. All trips can also be customized for private groups.';
      case 'solo': return 'Perfect for solo travelers! These adventures are designed for independent explorers. Groups can also book these itineraries on custom dates.';
      default: return 'Discover curated travel experiences designed for adventurers. Pay in full or choose flexible installment plans.';
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Header */}
      <div className="bg-gradient-to-b from-primary/5 to-transparent pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-4">
            {getPageTitle()}
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl">
            {getPageDescription()}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 mb-12">
        <div className="bg-white rounded-2xl shadow-sm border p-4 flex flex-col md:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search destinations..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="trips-search-input"
              />
            </div>
            <Button type="submit" className="bg-secondary hover:bg-secondary/90" data-testid="trips-search-btn">
              <Search className="h-4 w-4" />
            </Button>
          </form>
          
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={tripType} onValueChange={setTripType} data-testid="trips-type-filter">
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Trip Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="both">Group & Solo</SelectItem>
                <SelectItem value="group">Group Tours Only</SelectItem>
                <SelectItem value="solo">Solo Adventures Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Trips Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-muted animate-pulse rounded-2xl h-96" />
            ))}
          </div>
        ) : trips.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground text-lg">No trips found matching your criteria.</p>
            <Button 
              onClick={() => { setSearch(''); setTripType('all'); }} 
              variant="outline" 
              className="mt-4"
            >
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {trips.map((trip, idx) => (
              <motion.div
                key={trip.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
              >
                <TripCard trip={trip} />
              </motion.div>
            ))}
          </div>
        )}

        {/* Payment Plans Info */}
        <div className="mt-16 bg-secondary/10 rounded-2xl p-8 text-center">
          <h3 className="font-heading text-2xl font-semibold text-primary mb-3">
            Flexible Payment Plans Available
          </h3>
          <p className="text-muted-foreground max-w-xl mx-auto mb-6">
            Can't pay in full? No problem! Split your trip cost into 3, 6, or 12 monthly payments. 
            Start your adventure now and pay over time.
          </p>
          <div className="bg-white/60 rounded-xl p-4 max-w-2xl mx-auto">
            <p className="text-sm font-medium text-primary">
              🎯 <strong>Traveling with a group?</strong> Solo adventure itineraries can be booked by groups on custom dates. 
              Contact us to arrange your private group experience!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
